import { motion, useInView } from 'framer-motion';
import React, { useRef, useState } from 'react';
import {
  FaAnchor,
  FaArrowRight,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaCrosshairs,
  FaFish,
  FaHandshake,
  FaShieldAlt,
  FaWater,
} from 'react-icons/fa';
import {
  boatWorldcatAerial,
  captainChristosYellowtail,
  heroBluefinCloseup,
  heroChristosGrouper,
} from '../assets/images/hero/imageAssets';
import SEOHead from '../components/SEOHead';
import { getFAQSchema, getOgImageUrl, OG_IMAGE_DIMENSIONS } from '../utils/seoSchemas';
import {
  BoatContent,
  BoatGrid,
  BoatImage,
  BoatSection,
  BodyText,
  CtaButton,
  CtaContainer,
  CtaSection,
  FaqAnswer,
  FaqContainer,
  FaqItem,
  FaqQuestion,
  FaqSection,
  GearItem,
  GearList,
  HeroEyebrow,
  HeroInner,
  HeroSubtitle,
  HeroTitle,
  PageHero,
  SectionEyebrow,
  SectionTitle,
  SpecItem,
  SpecLabel,
  SpecsGrid,
  SpecValue,
  StoryContent,
  StoryGrid,
  StoryImage,
  StorySection,
  ValueCard,
  ValueIcon,
  ValuesContainer,
  ValuesGrid,
  ValuesSection,
  ValueText,
  ValueTitle,
} from './styles/AboutPage.styles';

const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const FaqAccordion = ({ question, answer }: { question: string; answer: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqItem>
      <FaqQuestion onClick={() => setOpen(!open)}>
        {question}
        {open ? FaChevronUp({ size: 16 }) : FaChevronDown({ size: 16 })}
      </FaqQuestion>
      <FaqAnswer $open={open}>
        <p>{answer}</p>
      </FaqAnswer>
    </FaqItem>
  );
};

const ABOUT_FAQS = [
  {
    question: 'Do I need my own spearfishing gear?',
    answer:
      'No — we provide spearguns, wetsuits, masks, snorkels, fins, weight belts, and float lines. If you have your own gear and prefer to use it, bring it along. Just let us know ahead of time so we can plan accordingly.',
  },
  {
    question: 'Do I need a fishing license?',
    answer:
      'Yes. A valid California fishing license with an ocean enhancement stamp is required for all participants — spearfishing and rod-and-reel. You can purchase one online at the California Department of Fish and Wildlife website. We can help you get set up if it\'s your first time.',
  },
  {
    question: "What's the best time of year to fish in SoCal?",
    answer:
      'The prime window is May through October, when water temps are warmest and visibility is best. White Seabass run early season (May–July), Yellowtail peak mid-summer (July–September), and Bluefin Tuna show up offshore late summer into fall. Calico Bass and Halibut are available year-round. We adjust our trips to target whatever is running.',
  },
  {
    question: "I've never been spearfishing. Can I still book?",
    answer:
      "Absolutely. Many of our guests are first-timers. Captain Christos will brief you on the basics — breath-hold technique, safety, how to use the speargun, species ID — and guide you through the entire experience. You'll be surprised how quickly you pick it up.",
  },
  {
    question: "What's the difference between open party and private charter?",
    answer:
      'Open party trips share the boat with other anglers — you book individual spots (max 6 per trip). Private charters reserve the entire boat for your group with a custom itinerary. Private charters let you choose your targets, set the schedule, and fish with just your crew.',
  },
  {
    question: 'Where do you fish?',
    answer:
      "We access the best waters in Southern California: Catalina Island, San Clemente Island, the Horseshoe Kelp, Palos Verdes reefs, offshore kelp paddies, and oil rig structure. Captain Christos reads the conditions daily and picks the spot based on what's running — not where everyone else is going.",
  },
  {
    question: 'What happens if the weather is bad?',
    answer:
      "Safety comes first. If conditions are unsafe, we'll reschedule your trip at no extra cost. We monitor weather, swell, and ocean conditions closely and will reach out 24–48 hours ahead if there's a concern.",
  },
  {
    question: 'How do I get to the boat?',
    answer:
      "We depart from San Pedro, CA — 224 Whalers Walk, 90731. Free parking is available at the dock. We'll send you detailed directions and a pin drop when your booking is confirmed.",
  },
];

const AboutPage = () => {
  return (
    <>
      <SEOHead
        title="About Fussaki — Captain Christos | San Pedro, CA"
        description="Meet Captain Christos and the WorldCat 295CC. Born to hunt, built to fish — spearfishing and sportfishing charters across Southern California since day one."
        canonical="https://fussaki.com/about"
        ogImage={getOgImageUrl(heroChristosGrouper)}
        ogImageWidth={OG_IMAGE_DIMENSIONS.heroChristosGrouper.width}
        ogImageHeight={OG_IMAGE_DIMENSIONS.heroChristosGrouper.height}
        jsonLd={getFAQSchema(ABOUT_FAQS)}
      />

      {/* ─── Hero Banner ─── */}
      <PageHero $bgImage={heroChristosGrouper} role="img" aria-label="Captain Christos holding a large Grouper catch">
        <HeroInner>
          <HeroEyebrow>About Fussaki</HeroEyebrow>
          <HeroTitle>Born to Hunt. <span>Built to Fish.</span></HeroTitle>
          <HeroSubtitle>
            Fussaki Spearfishing & Sportfishing Charters — small crew, expert captain,
            Southern California's best waters. Based out of San Pedro, CA.
          </HeroSubtitle>
        </HeroInner>
      </PageHero>

      {/* ─── Our Story ─── */}
      <StorySection>
        <StoryGrid>
          <AnimatedSection>
            <StoryImage>
              <img
                src={captainChristosYellowtail}
                alt="Captain Christos with a haul of Yellowtail — Fussaki Spearfishing Charters"
              />
            </StoryImage>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <StoryContent>
              <SectionEyebrow>Our Story</SectionEyebrow>
              <SectionTitle>From the Water, For the Water</SectionTitle>
              <BodyText>
                Fussaki was born out of a simple idea: build the charter that Captain Christos
                would want to book himself. Too many operations run like floating tourist buses —
                packed decks, rushed schedules, crews who treat spearfishing like an afterthought.
                Fussaki is the opposite.
              </BodyText>
              <BodyText>
                Captain Christos grew up hunting the rocky points and kelp beds of Southern California.
                After years of studying these waters — tracking fish patterns, reading conditions,
                learning where the White Seabass hold in the kelp, where the Yellowtail stack on the
                iron, where the Bluefin push through offshore — he launched Fussaki to share that
                knowledge with a small crew of serious anglers and hunters.
              </BodyText>
              <BodyText>
                Today Fussaki runs out of San Pedro, accessing some of the most productive spearfishing
                and sportfishing territory on the West Coast: Catalina Island, San Clemente Island,
                the Horseshoe Kelp, Palos Verdes reefs, offshore kelp paddies, and oil rig structure.
                Every trip is captain-led, conditions-driven, and built around putting you on fish.
              </BodyText>
            </StoryContent>
          </AnimatedSection>
        </StoryGrid>
      </StorySection>

      {/* ─── Values ─── */}
      <ValuesSection>
        <ValuesContainer>
          <AnimatedSection>
            <SectionEyebrow style={{ textAlign: 'center' }}>What We Stand For</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              More Than a Charter. It's a Standard.
            </SectionTitle>
          </AnimatedSection>
          <ValuesGrid>
            {[
              {
                icon: FaShieldAlt({ size: 28 }),
                title: 'Safety First, Always',
                text: 'Full first aid and O2 on board. We brief every trip and we don\'t cut corners — your safety is non-negotiable whether you\'re spearfishing or on the rod.',
              },
              {
                icon: FaFish({ size: 28 }),
                title: 'Respect the Ocean',
                text: 'We practice selective harvest. We target legal, sustainable species and teach our guests to do the same. The ocean gives us everything — we protect it.',
              },
              {
                icon: FaHandshake({ size: 28 }),
                title: 'No Egos, Just Results',
                text: 'Whether you\'re a seasoned spearfisher or picking up a rod for the first time, you get the same level of respect and attention. We\'re here to put you on fish.',
              },
              {
                icon: FaCrosshairs({ size: 28 }),
                title: 'Expert Local Knowledge',
                text: 'Captain Christos reads the conditions — swell, current, visibility, water temp — and makes the call on where to fish. That\'s how you find White Seabass, Yellowtail, and Bluefin.',
              },
              {
                icon: FaWater({ size: 28 }),
                title: 'Freedive-Ready',
                text: 'Every spearfishing trip is built around breath-hold diving. Whether you\'re an experienced freediver or just getting started, we\'ll get you comfortable and confident in the water.',
              },
              {
                icon: FaAnchor({ size: 28 }),
                title: 'Clean Your Catch',
                text: 'We don\'t just put you on fish — we help you take it home. Catch is bled, iced, and handled right on the boat so you leave with sushi-grade quality every time.',
              },
            ].map((v, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <ValueCard>
                  <ValueIcon>{v.icon}</ValueIcon>
                  <ValueTitle>{v.title}</ValueTitle>
                  <ValueText>{v.text}</ValueText>
                </ValueCard>
              </AnimatedSection>
            ))}
          </ValuesGrid>
        </ValuesContainer>
      </ValuesSection>

      {/* ─── The Boat ─── */}
      <BoatSection>
        <BoatGrid>
          <AnimatedSection>
            <BoatContent>
              <SectionEyebrow>The Vessel</SectionEyebrow>
              <SectionTitle>WorldCat 295CC — Built for the Hunt</SectionTitle>
              <BodyText>
                The WorldCat 295CC is a 29.5-foot center console catamaran built for offshore
                performance and all-day comfort. Twin outboards deliver the power and range to
                reach Catalina, San Clemente Island, and distant offshore structure — and the
                catamaran hull handles rough water better than any monohull in its class.
              </BodyText>

              <SpecsGrid>
                <SpecItem>
                  <SpecValue>29.5'</SpecValue>
                  <SpecLabel>Length Overall</SpecLabel>
                </SpecItem>
                <SpecItem>
                  <SpecValue>6</SpecValue>
                  <SpecLabel>Max Anglers</SpecLabel>
                </SpecItem>
                <SpecItem>
                  <SpecValue>Twin</SpecValue>
                  <SpecLabel>Suzuki Outboards</SpecLabel>
                </SpecItem>
                <SpecItem>
                  <SpecValue>All Day</SpecValue>
                  <SpecLabel>Offshore Range</SpecLabel>
                </SpecItem>
              </SpecsGrid>

              <GearList>
                <GearItem>{FaCheck({ size: 14 })} Dedicated speargun storage racks</GearItem>
                <GearItem>{FaCheck({ size: 14 })} Insulated fish holds with ice</GearItem>
                <GearItem>{FaCheck({ size: 14 })} Easy swim step entry/exit for divers</GearItem>
                <GearItem>{FaCheck({ size: 14 })} GPS, fishfinder, and marine radio</GearItem>
                <GearItem>{FaCheck({ size: 14 })} Full safety kit: O2, first aid, float lines</GearItem>
                <GearItem>{FaCheck({ size: 14 })} Rod holders and live bait tank for sportfishing</GearItem>
                <GearItem>{FaCheck({ size: 14 })} Shaded center console and cooler</GearItem>
              </GearList>
            </BoatContent>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <BoatImage>
              <img
                src={boatWorldcatAerial}
                alt="WorldCat 295CC — Fussaki charter vessel cruising Southern California waters"
              />
            </BoatImage>
          </AnimatedSection>
        </BoatGrid>
      </BoatSection>

      {/* ─── FAQ ─── */}
      <FaqSection>
        <FaqContainer>
          <AnimatedSection>
            <SectionEyebrow style={{ textAlign: 'center' }}>FAQ</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              Common Questions
            </SectionTitle>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            {ABOUT_FAQS.map((faq, i) => (
              <FaqAccordion key={i} question={faq.question} answer={faq.answer} />
            ))}
          </AnimatedSection>
        </FaqContainer>
      </FaqSection>

      {/* ─── CTA ─── */}
      <CtaSection $bgImage={heroBluefinCloseup} role="img" aria-label="Close-up of a Bluefin Tuna caught on a Fussaki charter">
        <AnimatedSection>
          <CtaContainer>
            <SectionEyebrow style={{ textAlign: 'center' }}>Ready to Fish?</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              The Water's Waiting.
            </SectionTitle>
            <BodyText style={{ textAlign: 'center', maxWidth: 550, margin: '0 auto' }}>
              Check live availability, pick your date, and lock in your spot.
              Open party and private charter options available.
            </BodyText>
            <div style={{ textAlign: 'center' }}>
              <CtaButton to="/book">
                Book Your Trip {FaArrowRight({ size: 14 })}
              </CtaButton>
            </div>
          </CtaContainer>
        </AnimatedSection>
      </CtaSection>
    </>
  );
};

export default AboutPage;
